'use client';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  FC,
  SVGProps,
} from 'react';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import {
  AATConfig,
  AFSConfig,
  AllIsWellConfig,
  AumeConfig,
  CalabriConfig,
  CommunityRXConfig,
  CosmoPrepConfig,
  CWJCConfig,
  DefaultConfig,
  ElevateLearningConfig,
  HealingConfig,
  LeverageConfig,
  LiftRAndRConfig,
  ProjectRestartConfig,
  SD171Config,
  SeedlyngConfig,
  UpstartTaxConfig,
  WealthWithLeeConfig,
  WorkforceConfig,
  BriidgeTheGapConfig,
  CodeForChangeConfig,
  MentalMattersConfig,
} from '@/config';

export type LogoSrc = { src: string }; // current PNG import shape
export type LogoSvg = FC<SVGProps<SVGSVGElement>>; // an inline SVG component

// TODO::Set colors differently
export interface Config {
  name: string;
  allowPersonalCourses: boolean;
  logo: LogoSrc | LogoSvg;
  colors: {
    brand_primary: string;
    brand_primary_hover: string;
    brand_primary_highlight: string;
    brand_primary_mid: string;
    brand_primary_dark: string;
    brand_secondary: string;
  };
}

const ConfigContext = createContext<Config | null>(null);

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<Config | null>(null);

  useEffect(() => {
    const loadConfig = () => {
      const hostname = window.location.hostname;

      const configMap: Record<string, Config> = {
        sd171: SD171Config,
        aeyl: WorkforceConfig,
        seedlyng: SeedlyngConfig,
        demo: DefaultConfig,
        afs: AFSConfig,
        projectrestart: ProjectRestartConfig,
        liftrandr: LiftRAndRConfig,
        upstarttax: UpstartTaxConfig,
        aboveallthings: AATConfig,
        leverage: LeverageConfig,
        elevatelearning: ElevateLearningConfig,
        cosmoprep: CosmoPrepConfig,
        healing: HealingConfig,
        calabri: CalabriConfig,
        aume: AumeConfig,
        alliswellhca: AllIsWellConfig,
        christianjobcorpstx: CWJCConfig,
        communityrxhelp: CommunityRXConfig,
        wealthwithlee: WealthWithLeeConfig,
        briidgethegap: BriidgeTheGapConfig,
        pathfinder: ProjectRestartConfig,
        thecodeforchange: CodeForChangeConfig,
        mentalmatters: MentalMattersConfig,
      };

      const config =
        Object.entries(configMap).find(([key]) => hostname.includes(key))?.[1] || DefaultConfig;
      setConfig(config);

      document.documentElement.style.setProperty('--color-brand', config.colors.brand_primary);
      document.documentElement.style.setProperty(
        '--color-brand-highlight',
        config.colors.brand_primary_highlight,
      );
      document.documentElement.style.setProperty(
        '--color-brand-mid',
        config.colors.brand_primary_mid,
      );
      document.documentElement.style.setProperty(
        '--color-brand-dark',
        config.colors.brand_primary_dark,
      );
      document.documentElement.style.setProperty(
        '--color-brand-secondary',
        config.colors.brand_secondary,
      );

      document.documentElement.style.setProperty(
        '--color-brand-hover',
        config.colors.brand_primary_hover,
      );
    };

    loadConfig();
  }, []);

  // Hydrate user progress from IndexedDB (fallback to network) on initial mount
  useEffect(() => {
    void UserProgressStorageService.validateUserCourseProgressCookie();
  }, []);

  if (!config) {
    return <div>Loading...</div>;
  }

  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
};

export const useConfig = (): Config | null => useContext(ConfigContext);
